import { Fragment } from "react";
import { setupLineSegments } from "../lib/setupStats";
import { CANDLE_GLOSSARY_URL, PATTERN_GLOSSARY_URL } from "../lib/setupGlossary";
import type { Setup } from "../types";
import type { SetupCandle, SetupPattern } from "../lib/setup";

/**
 * The reveal's setup sentence (`setupStats.ts`), rendered with the candle and
 * pattern terms linked out to a glossary entry (GRA-141 follow-up). Reads
 * identically to `setupLine()` when flattened to text: both build on
 * `setupLineSegments`, so the two can never say different things.
 *
 * Structure phrases ("in an uptrend", "no single broader structure") and the
 * sentence's connective words ("in the recent candles", "last candle a")
 * stay plain: only the two
 * labels a player might not recognise (the candle shape, the multi-candle
 * pattern) are worth a link.
 */
export function SetupLine({ setup }: { setup: Setup | undefined }) {
  const segments = setupLineSegments(setup);
  if (!segments) return null;

  return (
    <>
      {segments.map((segment, i) => {
        if (!segment.term) return <Fragment key={i}>{segment.text}</Fragment>;
        const href =
          segment.term === "candle"
            ? CANDLE_GLOSSARY_URL[setup!.candle as SetupCandle]
            : PATTERN_GLOSSARY_URL[setup!.pattern as SetupPattern];
        return (
          <a
            key={i}
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            className="text-accent underline decoration-accent/40 underline-offset-2 hover:decoration-accent"
          >
            {segment.text}
          </a>
        );
      })}
    </>
  );
}
